import { useState } from 'react';
import type { FormEvent } from 'react';

import { supabase } from './supabase-client';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<{ text: string; kind: 'status' | 'error' } | null>(null);
  const [pending, setPending] = useState(false);

  async function signIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!supabase) return;
    setPending(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error)
      setMessage({
        text: 'Connexion refusée. Vérifie tes identifiants ou le statut de ton compte.',
        kind: 'error',
      });
    else {
      await supabase.rpc('log_auth_event', { event_action: 'auth.signed_in', event_origin: 'web' });
      setMessage({ text: 'Connexion établie.', kind: 'status' });
    }
    setPending(false);
  }

  async function recoverAccount() {
    if (!supabase || !email.endsWith('@alba-demo.invalid')) {
      setMessage({
        text: 'La récupération est disponible uniquement pour les comptes de démonstration configurés.',
        kind: 'status',
      });
      return;
    }
    setPending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    setMessage(
      error
        ? { text: 'La récupération n’a pas pu être demandée.', kind: 'error' }
        : {
            text: 'La demande de récupération a été enregistrée pour ce compte de démonstration.',
            kind: 'status',
          },
    );
    setPending(false);
  }

  return (
    <main className="auth-page">
      <section className="auth-story" aria-label="Présentation ALBA Direction">
        <div>
          <p className="eyebrow">ALBA Direction</p>
          <h2>Décider avec une vision claire.</h2>
          <p>
            Réunissez les sujets, les décisions et les actions de la Direction dans un espace sobre,
            traçable et sécurisé.
          </p>
        </div>
        <p className="auth-story__footer">Une seule source de vérité pour avancer ensemble.</p>
      </section>
      <section className="auth-card" aria-labelledby="auth-title">
        <p className="eyebrow">ALBA Direction</p>
        <h1 id="auth-title">Connexion sécurisée</h1>
        <p>
          Chaque accès est individuel. Les comptes de démonstration utilisent uniquement des
          adresses non routables.
        </p>
        <form onSubmit={(event) => void signIn(event)} className="auth-form">
          <label htmlFor="email">Adresse email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />
          <label htmlFor="password">Mot de passe</label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
          <button type="submit" disabled={pending}>
            {pending ? 'Connexion en cours' : 'Se connecter'}
          </button>
        </form>
        <button
          className="text-button"
          type="button"
          onClick={() => void recoverAccount()}
          disabled={pending}
        >
          Récupérer mon compte
        </button>
        {message && (
          <p
            className={message.kind === 'error' ? 'error-state' : 'status-message'}
            role={message.kind === 'error' ? 'alert' : 'status'}
          >
            {message.text}
          </p>
        )}
      </section>
    </main>
  );
}
